import { useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'

export default function Privacy() {
  useEffect(() => {
    document.body.classList.add('v2-legal-page')
    return () => document.body.classList.remove('v2-legal-page')
  }, [])

  return (
    <>
      <SEO
        title="Privacy Policy - shimmertech.net"
        description="shimmertech.net privacy policy. How we collect, use, and protect your data for FDE work, LLM infrastructure, and production AI services."
        path="/privacy"
      />

      <div className="od-home od-legal-shell">
        <SiteNav />

        <main className="od-legal-main">
          <header className="od-legal-hero">
            <h1>Privacy Policy</h1>
            <p>Last updated: April 27, 2026</p>
          </header>

          <div className="od-legal-sections">
            <section className="od-legal-section">
              <h2>1. Introduction</h2>
              <p>
                shimmertech.net ("we," "our," or "us") operates the shimmertech.net website and provides forward-deployed engineering, LLM infrastructure, and production AI services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services. By accessing or using our platform, you agree to this Privacy Policy.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>2. Information We Collect</h2>
              <h3>2.1 Information You Provide</h3>
              <ul>
                <li>Account information: name, email address, and password (or magic-link identifier) when you register.</li>
                <li>Payment information: billing details processed through our third-party payment provider. We do not store your full credit card number.</li>
                <li>Project data: source code, configuration files, documents, datasets, prompts, schemas, and other content needed for an engagement.</li>
                <li>AI system metadata: model providers, endpoints, evaluation records, routing configuration, and integration details you ask us to support.</li>
                <li>Communications: messages you send to us via email or support channels.</li>
              </ul>
              <h3>2.2 Information Collected Automatically</h3>
              <ul>
                <li>Usage data: API calls, model routing events, logs, evaluation runs, project state changes, and service usage.</li>
                <li>Operational activity: actions taken in supported systems, status, resource usage, timestamps, and audit records.</li>
                <li>AI gateway telemetry: when you route AI calls through shimmertech.net's gateway (BYOK or otherwise), we record provider, model, token counts, and cost — but not prompt or completion content unless you opt in to debug logging.</li>
                <li>Device information: browser type, operating system, IP address, and device identifiers for website or service sessions.</li>
                <li>Cookies and similar technologies: we use cookies on shimmertech.net to maintain sessions and improve your experience.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>3. How We Use Your Information</h2>
              <ul>
                <li>Provide, maintain, and improve our FDE, LLM infrastructure, and production AI services.</li>
                <li>Design, build, evaluate, deploy, and operate AI systems you ask us to support.</li>
                <li>Provide observability, logs, spend tracking, evaluation records, and audit trails for supported systems.</li>
                <li>Process payments and manage your subscription, plan, and metered usage.</li>
                <li>Send service-related communications, such as delivery updates, budget alerts, integration notices, and security notices.</li>
                <li>Respond to your inquiries and provide customer support.</li>
                <li>Detect, prevent, and address technical issues, abuse, fraud, or security threats, including prompt-injection or compromised integration risk.</li>
                <li>Comply with legal obligations.</li>
              </ul>
              <p>
                We do not use your source code, prompts, or model outputs to train any AI model — ours or third-party.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>4. Source Code, Project Data, and Agent Artifacts</h2>
              <p>
                Your source code, prompts, documents, datasets, and configuration are processed solely for the purpose of providing the services you request. We do not sell, share, or use your project data for any purpose other than delivering the engagement or service. Project data is encrypted in transit and at rest where applicable, and is not retained longer than necessary for the agreed work.
              </p>
              <p>
                Artifacts produced during AI development — prompts, generated configuration, evaluation records, model outputs, runbooks, and integration notes — are treated with the same protections as source code and project data.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>5. Data Sharing and Disclosure</h2>
              <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
              <ul>
                <li><strong>Service providers:</strong> trusted third parties that assist us in operating our platform (cloud infrastructure, payment processing, analytics, email delivery).</li>
                <li><strong>AI model providers:</strong> when you route AI calls through our gateway with BYOK keys, request payloads pass to the provider you selected (Anthropic, OpenAI, Google, xAI, etc.) under that provider's privacy terms.</li>
                <li><strong>Legal requirements:</strong> when required by law, subpoena, or government request.</li>
                <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, with notice to affected users.</li>
                <li><strong>With your consent:</strong> when you explicitly authorize us to share information.</li>
              </ul>
              <p>
                We do not share project data with third-party AI vendors except as needed to operate the model provider or infrastructure you approve.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>6. Data Security</h2>
              <p>
                We implement industry-standard security measures including encryption (TLS 1.2+), isolated environments, network policies, encrypted storage, scoped credentials, access controls, optional spend caps, and regular security reviews. While we strive to protect your data, no method of transmission or storage is 100% secure.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>7. Data Retention</h2>
              <p>
                We retain your account information for as long as your account is active. Project data, logs, evaluation records, and operational audit trails are retained for the duration of your subscription or engagement unless otherwise agreed. You may request deletion of your account and associated data at any time by contacting us.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>8. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul>
                <li>Access, correct, or delete your personal information.</li>
                <li>Object to or restrict processing of your data.</li>
                <li>Export your data, including applicable operational audit records, in a portable format.</li>
                <li>Withdraw consent where processing is based on consent.</li>
                <li>Request credential rotation or revocation for integrations we support.</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at <a href="mailto:hi@shimmertech.net">hi@shimmertech.net</a>.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>9. Cookies</h2>
              <p>
                We use essential cookies on shimmertech.net to maintain sessions and improve your experience. We may also use analytics cookies to understand how the website and services are used. You can control cookie preferences through your browser settings.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>10. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of the platform after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>12. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our data practices, please contact us at <a href="mailto:hi@shimmertech.net">hi@shimmertech.net</a>.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  )
}
